import { useId } from 'react';

import { IconMultiClipboardBlue } from '@snorose/icons';

import { useToast } from '@/shared/hook';

import { INVALID_NAME_REGEX } from '@/feature/attachment/lib/attachment';
import type { UploadFile } from '@/feature/attachment/types';

import styles from './FileUploadSection.module.css';

const MAX_FILE_COUNT = 3;
const MAX_FILE_SIZE = 7 * 1024 * 1024;

export default function FileUploadSection({
  currentFileCount,
  setFiles,
}: {
  currentFileCount: number;
  setFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>;
}) {
  const id = useId();
  const { toast } = useToast();

  return (
    <label htmlFor={id} className={styles.container}>
      <input
        className={styles.srOnly}
        id={id}
        type='file'
        accept='image/*'
        onChange={(e) => {
          const selectedFiles = Array.from(e.target.files);

          if (selectedFiles.some((file) => !file.type.startsWith('image/'))) {
            e.target.value = '';

            toast.info('이미지만 업로드할 수 있어요');

            return;
          }

          if (currentFileCount + selectedFiles.length > MAX_FILE_COUNT) {
            e.target.value = '';

            toast.info(`이미지는 ${MAX_FILE_COUNT}개까지 업로드할 수 있어요`);

            return;
          }

          const selectedFileSizes = selectedFiles.map((file) => file.size);

          if (selectedFileSizes.some((size) => size > MAX_FILE_SIZE)) {
            e.target.value = '';

            toast.info(
              `${MAX_FILE_SIZE / (1024 * 1024)}MB 이하의 이미지만 업로드할 수 있어요`
            );

            return;
          }

          const invalidCharsFound = new Set();

          selectedFiles.forEach(({ name }) => {
            const matches = name.match(INVALID_NAME_REGEX);
            if (matches) {
              matches.forEach((char) => invalidCharsFound.add(char));
            }
          });

          if (invalidCharsFound.size > 0) {
            e.target.value = '';

            const invalidCharsString = Array.from(invalidCharsFound).join(', ');

            toast.info(
              `앗! 파일 이름에서 다음 문자를 지워주세요 ${invalidCharsString} `
            );

            return;
          }

          setFiles((prev) => [
            ...prev,
            ...selectedFiles.map((file) => ({ file, id: crypto.randomUUID() })),
          ]);

          e.target.value = '';
        }}
        multiple
      />

      <div className={styles.left}>
        <IconMultiClipboardBlue width={24} height={24} />
        <div>첨부파일</div>
      </div>

      <div className={styles.right}>
        <span className={styles.placeholder}>파일을 첨부하세요</span>
      </div>
    </label>
  );
}
